import React, { useState } from 'react';
import { Box, Container, Grid, Chip, Stack, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ProductCard from '../components/ProductCard';
import SectionHeading from '../components/SectionHeading';
import { gradients } from '../theme/theme';
import ComputerIcon from '@mui/icons-material/Computer';
import RouterIcon from '@mui/icons-material/Router';
import LanguageIcon from '@mui/icons-material/Language';
import MemoryIcon from '@mui/icons-material/Memory';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { trackEvent } from '../utils/analytics';

const WHATSAPP_NUMBER = '905325525984';

const CATEGORY_ICON = {
  computer: ComputerIcon,
  modem: RouterIcon,
  internet: LanguageIcon,
  electronics: MemoryIcon,
};

// Catalog: example products we can source — not limited to specific brands
const PRODUCTS = [
  { key: 'laptop', category: 'computer' },
  { key: 'desktop', category: 'computer' },
  { key: 'keyboardmouse', category: 'computer' },
  { key: 'printer', category: 'computer' },
  { key: 'router', category: 'modem' },
  { key: 'wifi6', category: 'modem' },
  { key: 'mesh', category: 'modem' },
  { key: 'fiber', category: 'internet' },
  { key: 'ethernet', category: 'internet' },
  { key: 'camera', category: 'electronics' },
  { key: 'harddrive', category: 'electronics' },
  { key: 'component', category: 'electronics' },
];

const CATEGORIES = ['all', 'computer', 'modem', 'internet', 'electronics'];

const ProductsPage = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');

  const visible = PRODUCTS.filter((p) => filter === 'all' || p.category === filter);

  const chipLabel = (cat) =>
    cat === 'all' ? t('products.catalog.all') : t(`products.catalog.categories.${cat}`);

  const ctaHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    t('products.catalog.cta.message')
  )}`;

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          eyebrow={t('products.eyebrow')}
          title={t('products.title')}
          subtitle={t('products.subtitle')}
          sx={{ mb: 4 }}
        />

        {/* "We source it for you" note */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            maxWidth: 820,
            mx: 'auto',
            mb: 6,
            px: { xs: 2.5, md: 3 },
            py: 2,
            borderRadius: 3,
            bgcolor: 'rgba(21,101,192,0.06)',
            border: '1px solid',
            borderColor: 'rgba(21,101,192,0.18)',
          }}
        >
          <InfoOutlinedIcon sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            {t('products.catalog.note')}
          </Typography>
        </Box>

        {/* Category filter */}
        <Stack
          direction="row"
          spacing={1.25}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{ mb: 6 }}
        >
          {CATEGORIES.map((cat) => {
            const selected = filter === cat;
            return (
              <Chip
                key={cat}
                label={chipLabel(cat)}
                onClick={() => {
                  setFilter(cat);
                  trackEvent('product_category_filter_click', { category: cat });
                }}
                sx={{
                  px: 1.5,
                  py: 2.25,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selected ? 'transparent' : 'divider',
                  color: selected ? 'white' : 'text.secondary',
                  background: selected
                    ? 'linear-gradient(135deg, #1565C0, #0D47A1)'
                    : 'background.paper',
                  boxShadow: selected ? '0 10px 22px -10px rgba(13,71,161,0.7)' : 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: selected ? 'transparent' : 'primary.main',
                    color: selected ? 'white' : 'primary.main',
                  },
                }}
              />
            );
          })}
        </Stack>

        <Grid container spacing={3.5}>
          <AnimatePresence mode="popLayout">
            {visible.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.key} component={motion.div} layout>
                <ProductCard
                  name={t(`products.catalog.items.${product.key}.name`)}
                  description={t(`products.catalog.items.${product.key}.desc`)}
                  image={`/products/${product.key}.jpg`}
                  categoryLabel={t(`products.catalog.categories.${product.category}`)}
                  icon={CATEGORY_ICON[product.category]}
                  index={index}
                />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>

        {/* "Can't find it?" CTA band */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              mt: { xs: 7, md: 10 },
              borderRadius: 6,
              px: { xs: 3, md: 8 },
              py: { xs: 5, md: 7 },
              background: gradients.primary,
              boxShadow: '0 30px 60px -30px rgba(13,71,161,0.6)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.4,
                background:
                  'radial-gradient(circle at 85% 15%, rgba(255,122,69,0.35), transparent 40%)',
              }}
            />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              sx={{ position: 'relative' }}
            >
              <Box sx={{ maxWidth: 640 }}>
                <Typography variant="h3" sx={{ color: 'white', mb: 1.5 }}>
                  {t('products.catalog.cta.title')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '1.05rem', lineHeight: 1.7 }}>
                  {t('products.catalog.cta.desc')}
                </Typography>
              </Box>
              <Button
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('product_request_click', { source: 'catalog_cta' })}
                size="large"
                startIcon={<WhatsAppIcon />}
                sx={{
                  flexShrink: 0,
                  bgcolor: 'white',
                  color: 'primary.dark',
                  px: 4,
                  py: 1.6,
                  fontSize: '1rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                }}
              >
                {t('products.catalog.cta.button')}
              </Button>
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProductsPage;
